/**
 * Banca › i movimenti del conto, a giorni (26/10/2026, os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: cosa è entrato e cosa è uscito?
 * La forma: l'estratto conto di un'app della banca. Un gruppo per giorno ("Oggi", "Ieri",
 * "Venerdì 23 ottobre"), una riga per movimento col logo di chi c'è dall'altra parte (Google,
 * Anthropic, Meta, Amazon… senza marchio le iniziali), a cosa è collegato in parole e quanto.
 * Il gesto sta sulla riga: "Collega" per un movimento senza documento; nel ⋯ "Scollega" e, per
 * quelli che si sono segnati da soli, "Rimetti da pagare/incassare".
 *
 * Prima (MovimentiTab.tsx, che resta per chi lo riapre): una tabella a sei colonne con filtri di
 * conto, date dal/al, ricerca, ordina e filtra. Tolti dalla vista: le date (un intervallo che
 * arriva da un link resta e si toglie con un tocco), la scelta del conto (si fa toccando il conto
 * in cima alla pagina), l'ordinamento. Restano la ricerca e "Filtra" (entrate o uscite, solo
 * senza documento), con gli stessi parametri dell'indirizzo di prima: i link di Fatture e Spese
 * ("Scegli il bonifico") aprono la lista giusta.
 */
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Icon } from '../../../components/ui/Icon';
import { BrandLogo, brandFor } from '../../../components/ui/BrandLogo';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { EmptyState } from '../../../components/ui/EmptyState';
import { OrdinaFiltra, useFiltri, type FiltroDef } from '../../../components/ui/OrdinaFiltra';
import { leggibile } from '../../../components/mobile/MobileKit';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { plurale } from '../adminApi';
import { CollegaPopover } from '../MovimentiTab';
import { aGruppi, giornoGruppo } from '../shared/AdminMobile';
import {
  COLLEGATO_LABEL, RIMETTI_LABEL, automaticiQuery, meseInParole, movimentiQuery, useBancaActions,
  type MovimentoConto, type PagamentoAutomatico,
} from './bancaApi';

const PAGINA = 40;

/** Le stesse chiavi dell'indirizzo di prima (`side`, `unmatched`). */
const FILTRI: FiltroDef[] = [
  {
    param: 'side', label: 'Entrate o uscite',
    opzioni: [
      { value: 'CREDIT', label: 'Solo le entrate' },
      { value: 'DEBIT', label: 'Solo le uscite' },
    ],
  },
  {
    param: 'unmatched', label: 'Documento collegato',
    opzioni: [{ value: '1', label: 'Solo i movimenti senza documento' }],
  },
];

export function MovimentiGiorni({ canWrite, showHistory, nomeConto, onTuttiIConti }: {
  canWrite: boolean;
  showHistory?: boolean;
  /** Il conto scelto toccando la sua scheda in cima (`?acc=`), per dirlo sopra la lista. */
  nomeConto?: string | null;
  onTuttiIConti?: () => void;
}) {
  const [accountId] = useUrlState<string>('acc', '');
  const [from, setFrom] = useUrlState<string>('from', '');
  const [to, setTo] = useUrlState<string>('to', '');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const filtri = useFiltri(FILTRI);
  const sideParam = filtri.valore('side');
  const side = sideParam === 'DEBIT' || sideParam === 'CREDIT' ? sideParam : '';
  const senzaDocumento = filtri.valore('unmatched') === '1';
  const [quante, setQuante] = useState(PAGINA);
  useEffect(() => { setQuante(PAGINA); }, [accountId, from, to, side, senzaDocumento, search, showHistory]);

  const q = useQuery({
    ...movimentiQuery({
      accountId: accountId || undefined, from: from || undefined, to: to || undefined, side, senzaDocumento,
      search: search.trim() || undefined, includeHistory: showHistory, page: 1, pageSize: quante,
    }),
    placeholderData: keepPreviousData,
  });
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const giorni = useMemo(() => aGruppi(items, (t) => t.settledOn ?? ''), [items]);

  // I pagamenti che si sono segnati da soli: sulla riga lo si dice, e nel ⋯ si rimettono com'erano.
  const { data: auto } = useQuery(automaticiQuery);
  const perMovimento = useMemo(() => new Map((auto?.items ?? []).map((v) => [v.bonifico.id, v])), [auto]);

  const cercando = !!search.trim() || filtri.attivi > 0 || !!from || !!to || !!accountId;

  return (
    <section className="ft-blocco" aria-label="Movimenti del conto">
      <div className="bn-mov-hd">
        <h2 className="ft-h2"><Icon name="swap" size={15} /> Movimenti {nomeConto ? <span className="ft-h2-sub">del {nomeConto}</span> : <span className="ft-h2-sub">di tutti i conti</span>}</h2>
        <div className="bn-mov-tools">
          <div className="tb-search bn-cerca">
            <Icon name="search" size={12} />
            <input aria-label="Cerca fra i movimenti" placeholder="Cerca un nome o una descrizione…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <OrdinaFiltra filtri={filtri} />
        </div>
      </div>

      {(nomeConto || from || to) && (
        <div className="bn-filtri-attivi">
          {nomeConto && onTuttiIConti && (
            <button type="button" className="bn-chip-via" onClick={onTuttiIConti}>Solo il {nomeConto} <Icon name="x" size={11} /></button>
          )}
          {(from || to) && (
            <button type="button" className="bn-chip-via" onClick={() => { setFrom(''); setTo(''); }}>
              {from && to ? `Dal ${fmtDate(from)} al ${fmtDate(to)}` : from ? `Dal ${fmtDate(from)}` : `Fino al ${fmtDate(to)}`} <Icon name="x" size={11} />
            </button>
          )}
        </div>
      )}

      {q.isLoading && !q.data ? <p className="ft-vuoto">Carico i movimenti…</p>
        : items.length === 0 ? (
          <EmptyState brand
            title={cercando ? 'Nessun movimento con questa ricerca' : 'Nessun movimento del conto'}
            description={senzaDocumento ? 'Ogni movimento è collegato a un documento.' : cercando ? 'Togli un filtro o cambia la ricerca.' : 'Collega Qonto e aggiorna la banca: i movimenti arrivano da soli.'}
            action={filtri.attivi > 0 ? <button className="btn" onClick={filtri.clear}>Togli i filtri</button> : undefined}
          />
        ) : (
          <>
            {giorni.map((g) => (
              <div key={g.chiave || 'senza'} className="ft-mese">
                <h3 className="ft-mese-hd">{g.chiave ? giornoGruppo(g.chiave) : 'Senza data'}</h3>
                <div className="ft-lista">
                  {g.items.map((t) => <RigaMovimento key={t.id} t={t} canWrite={canWrite} auto={perMovimento.get(t.id) ?? null} />)}
                </div>
              </div>
            ))}
            {total > items.length && (
              <button type="button" className="ft-mostra" disabled={q.isFetching} onClick={() => setQuante((n) => n + PAGINA)}>
                <Icon name="chevronD" size={13} /> {q.isFetching ? 'Carico…' : `Mostra altri ${Math.min(PAGINA, total - items.length)} movimenti, su ${plurale(total, 'movimento', 'movimenti')}`}
              </button>
            )}
          </>
        )}
    </section>
  );
}

/** A cosa è collegato, in parole: "Fattura di un cliente: 2026/180 di Greenvincible". */
function collegatoInParole(t: MovimentoConto): string | null {
  if (t.collegato) return `${COLLEGATO_LABEL[t.collegato.tipo]}${t.collegato.cosa ? `: ${t.collegato.cosa}` : ''}`;
  if (t.stipendio) {
    const chi = [t.stipendio.persona, t.stipendio.mese ? `di ${meseInParole(t.stipendio.mese)}` : null].filter(Boolean).join(' ');
    return `${t.stipendio.etichetta}${chi ? `, ${chi}` : ''}`;
  }
  return null;
}

function RigaMovimento({ t, canWrite, auto }: { t: MovimentoConto; canWrite: boolean; auto: PagamentoAutomatico | null }) {
  const { scollegaMovimento, annullaAutomatico } = useBancaActions();
  const [collega, setCollega] = useState(false);
  const entrata = t.side === 'CREDIT';
  const nome = leggibile(t.counterparty || t.label);
  const cosa = collegatoInParole(t);
  const etichetta = `${fmtDate(t.settledOn)}${t.amount != null ? ` · € ${fmtEur(t.amount, true)}` : ''} · ${nome}`;
  const menu: ActionItem[] = [
    { key: 'rimetti', label: auto ? RIMETTI_LABEL[auto.tipo] : '', icon: 'undo', onClick: () => auto && annullaAutomatico.mutate(auto), hidden: !canWrite || !auto },
    { key: 'scollega', label: 'Scollega il documento', icon: 'unlink', onClick: () => scollegaMovimento.mutate({ transactionId: t.id, label: etichetta }), hidden: !canWrite || !t.match || t.riservato },
  ];
  return (
    <div className="bn-mov">
      <BrandLogo brand={brandFor(t.counterparty) ?? brandFor(t.label)} name={nome} size={32} />
      <span className="ft-chi">
        <span className="t" title={t.label}>{nome}</span>
        <span className="s">
          {cosa ?? <span className="bn-nessuno">nessun documento collegato</span>}
          {auto && <span className="bn-auto" title={`Segnato da solo ${fmtDate(auto.segnataIl.slice(0, 10))}: importo, nome e data tornavano`}>segnato da solo</span>}
        </span>
      </span>
      <span className={`bn-mov-cifra${entrata ? ' is-entrata' : ''}`}>
        {t.amount == null
          ? <span className="mute" title="Gli importi degli stipendi li vede solo chi amministra">riservato</span>
          : <>{entrata ? '+' : '−'} € {fmtEur(t.amount, true)}</>}
      </span>
      <span className="ft-azioni">
        {canWrite && !t.match && !t.stipendio && !t.riservato && (
          <button className="btn btn-sm btn-ghost" title="Collega a una fattura, una tassa o una spesa ancora da pagare" onClick={() => setCollega((v) => !v)}>
            <Icon name="link" size={10} /> Collega
          </button>
        )}
        <ActionsMenu items={menu} label={`Azioni sul movimento ${etichetta}`} />
        {collega && <CollegaPopover t={t} onClose={() => setCollega(false)} />}
      </span>
    </div>
  );
}
